import { useEffect, useSyncExternalStore } from 'react';

type CozyTheme = 'light' | 'dark';

const STORAGE_KEY = 'ys-public-theme';
const listeners = new Set<() => void>();

const readPreferredTheme = (): CozyTheme => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  const prefersDark = window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
  return prefersDark ? 'dark' : 'light';
};

let currentTheme: CozyTheme = typeof window === 'undefined' ? 'light' : readPreferredTheme();

const syncDom = () => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = currentTheme;
  root.style.colorScheme = currentTheme;
};

const emit = () => listeners.forEach((listener) => listener());

const setThemeInternal = (next: CozyTheme) => {
  currentTheme = next;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, next);
  }
  syncDom();
  emit();
};

syncDom();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, () => currentTheme, () => currentTheme);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = (event: MediaQueryListEvent) => {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') return;
      setThemeInternal(event.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', handle);
    return () => media.removeEventListener('change', handle);
  }, []);

  const setTheme = (next: CozyTheme) => setThemeInternal(next);
  const toggleTheme = () => setThemeInternal(theme === 'light' ? 'dark' : 'light');

  return { theme, setTheme, toggleTheme } as const;
}
